import {
  Banknote,
  CreditCard,
  Coffee,
  FileText,
  HandCoins,
  Landmark,
  LayoutDashboard,
  Monitor,
  MonitorPlay,
  Printer,
  Receipt,
  ScrollText,
  Settings,
  Store,
  Ticket,
  Trophy,
  Users,
  Wallet,
  CalendarClock,
} from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * Sidebar definition.
 *
 * `permission` gates visibility: an employee who lacks it never sees the entry,
 * which is how the owner "changes their view of the system". The permission
 * strings mirror the keys exported by @gc/shared; they are typed as plain
 * strings here so this module does not block on that package building.
 *
 * `translationKey` resolves against the `nav.*` namespace in the locale files.
 */
export interface NavItem {
  id: string;
  path: string;
  translationKey: string;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  permission: string | null;
}

/**
 * A titled group of entries (owner, 2026-08-14: "the different tabs are
 * sectioned … under one category that can be collapsed and expanded").
 *
 * `translationKey: null` marks the ungrouped strip at the top — Dashboard has
 * no family and a one-item section with a header would be a heavier way of
 * saying nothing. In the drawer a null-key section renders as bare rows; a
 * titled one renders as a collapsible group. The rail (icon column) renders
 * every section flat with a hairline between groups — icons cannot carry a
 * heading, but the grouping still reads.
 */
export interface NavSection {
  id: string;
  /** `nav.sections.*`, or null for the bare ungrouped strip. */
  translationKey: string | null;
  items: readonly NavItem[];
}

/**
 * The menu, grouped the way the venue thinks about the work:
 *
 * - **Floor** — running the room: machines, food, passes, bookings, screens.
 * - **Money** — the till's reading: what is owed, what was taken, where cash
 *   sits. `cash-drawer` deliberately rides here even though it has no gate.
 * - **Customers** — the people and what they see of themselves.
 * - **Management** — the back office: analysis, stock, hardware, settings.
 *
 * Order within each section keeps the incumbent system's habit order: the
 * entries a cashier hits all shift come before the ones touched weekly.
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'overview',
    translationKey: null,
    items: [
      {
        id: 'dashboard',
        path: '/dashboard',
        translationKey: 'nav.dashboard',
        icon: LayoutDashboard,
        permission: 'dashboard.view',
      },
    ],
  },
  {
    id: 'floor',
    translationKey: 'nav.sections.floor',
    items: [
      {
        id: 'devices',
        path: '/devices',
        translationKey: 'nav.devices',
        icon: Monitor,
        permission: null,
      },
      {
        id: 'cafe',
        path: '/cafe',
        translationKey: 'nav.cafe',
        icon: Coffee,
        permission: 'cafe.order',
      },
      {
        // SPEC §6.1 — Boot Camp day passes. Namespaced under the area bundle,
        // NOT `nav.*`, for the reason `outstanding` gives below.
        id: 'day-passes',
        path: '/day-passes',
        translationKey: 'dayPasses.nav',
        icon: Ticket,
        // Selling a pass is a counter action, so it sits with opening a session.
        permission: 'sessions.manage',
      },
      {
        id: 'reservations',
        path: '/bookings',
        translationKey: 'nav.reservations',
        icon: CalendarClock,
        permission: null,
      },
      {
        id: 'screens',
        path: '/screens',
        translationKey: 'nav.screens',
        icon: MonitorPlay,
        // The screen and GET /screens both answer to media.manage. Gated on
        // devices.manage, nine employees had an entry that opened "no
        // permission".
        permission: 'media.manage',
      },
    ],
  },
  {
    id: 'money',
    translationKey: 'nav.sections.money',
    items: [
      {
        id: 'outstanding',
        path: '/outstanding',
        // Namespaced under the area bundle, NOT `nav.*`: an area file must not
        // add a top-level `nav` key or it shadows the core namespace.
        translationKey: 'outstanding.nav',
        icon: HandCoins,
        // The money owed rather than the money taken, but it is the same reading.
        permission: 'bills.view',
      },
      {
        id: 'bills',
        path: '/bills',
        translationKey: 'nav.bills',
        icon: Receipt,
        // Spec is explicit: without this permission the Bills tab is not shown.
        permission: 'bills.view',
      },
      {
        // The venue's one cash drawer (2026-10-12), in place of Box Balance
        // and Shifts. No gate: everyone may see it is open; the screen shows
        // each control only to whoever the API lets use it. Area bundle key,
        // same rule as `outstanding`.
        id: 'cash-drawer',
        path: '/cash-drawer',
        translationKey: 'cashDrawer.nav',
        icon: Banknote,
        permission: null,
      },
      {
        // Wave 3 §12.1 — the safe and the unified Bonds ledger.
        id: 'bonds',
        path: '/bonds',
        translationKey: 'nav.bonds',
        icon: Landmark,
        permission: 'safe.view',
      },
      {
        id: 'expenses',
        path: '/expenses',
        translationKey: 'nav.expenses',
        icon: Wallet,
        // What every /expenses and /incomes route requires. reports.view hid
        // the screen from the one employee granted exactly this.
        permission: 'finance.manageExpenses',
      },
    ],
  },
  {
    id: 'customers',
    translationKey: 'nav.sections.customers',
    items: [
      {
        id: 'customers',
        path: '/customers',
        translationKey: 'nav.customers',
        icon: Users,
        permission: 'customers.manage',
      },
      {
        id: 'cards',
        path: '/cards',
        translationKey: 'nav.cards',
        icon: CreditCard,
        permission: 'customers.manage',
      },
      {
        id: 'portal-content',
        path: '/portal-content',
        // Lives in the portalContent area bundle, not the core `nav.*`
        // namespace — same shadowing rule as `outstanding` above.
        translationKey: 'portalContent.nav',
        icon: Trophy,
        permission: 'portal.manage',
      },
    ],
  },
  {
    id: 'management',
    translationKey: 'nav.sections.management',
    items: [
      {
        id: 'reports',
        path: '/reports',
        translationKey: 'nav.reports',
        icon: FileText,
        permission: 'reports.view',
      },
      {
        id: 'store',
        path: '/store',
        translationKey: 'nav.store',
        icon: Store,
        permission: 'products.manage',
      },
      {
        id: 'printers',
        path: '/printers',
        translationKey: 'nav.printers',
        icon: Printer,
        permission: 'settings.manage',
      },
      {
        // SPEC §3 — the audit log, read-only. Its own key: the owner decides
        // who may read what everyone else did. Area bundle key, like `outstanding`.
        id: 'audit-log',
        path: '/audit-log',
        translationKey: 'auditLog.nav',
        icon: ScrollText,
        permission: 'audit.view',
      },
      {
        id: 'settings',
        path: '/settings',
        translationKey: 'nav.settings',
        icon: Settings,
        permission: 'settings.manage',
      },
    ],
  },
] as const;

/**
 * Settings sub-navigation (SPEC §6), GROUPED.
 *
 * The flat list of thirteen reached the length where the owner stopped reading
 * it and started hunting (his words, 2026-09-20: make it "more organized,
 * sectioned"). These five groups are the questions he actually arrives with —
 * what the venue is, what it charges and prints, what a customer sees on a
 * kiosk, what the machines are, who works here.
 *
 * NO GROUP COLLAPSES, deliberately: a heading here is a label, not a door. An
 * owner who does not already know where a setting lives must be able to see
 * every setting the app has without opening anything — that is the whole
 * point of grouping them, and a collapsed group hides exactly the setting he
 * came looking for. The search box above the groups is the shortcut. (On a
 * phone the WHOLE list folds behind one menu button — 2026-09-23, because
 * unfolded it was the only thing on the screen — but open, it is still every
 * group, every entry.)
 *
 * WHAT LEFT: Categories and Products. They are cafe screens that the settings
 * routes only redirect to, and they were never settings; they are replaced by
 * one exit link below the groups.
 */
export interface SettingsNavItem {
  id: string;
  path: string;
  /** Resolves against `settings.nav.*`. */
  translationKey: string;
}

export interface SettingsNavGroup {
  id: string;
  /** Resolves against `settings.nav.groups.*`. */
  translationKey: string;
  items: readonly SettingsNavItem[];
}

export const SETTINGS_NAV_GROUPS: readonly SettingsNavGroup[] = [
  {
    id: 'venue',
    translationKey: 'settings.nav.groups.venue',
    items: [
      // `/settings/general`, not `/settings/venue`: the area's landing route,
      // and every bookmark and in-app redirect points at it.
      { id: 'venue', path: '/settings/general', translationKey: 'settings.general.title' },
      { id: 'loyalty', path: '/settings/loyalty', translationKey: 'settings.nav.loyalty' },
    ],
  },
  {
    id: 'money',
    translationKey: 'settings.nav.groups.money',
    items: [
      { id: 'printing', path: '/settings/printing', translationKey: 'settings.nav.printing' },
      // Was "Electronics", which named the hardware nobody sells here rather
      // than the thing configured: e-payment provider credentials.
      { id: 'payments', path: '/settings/payments', translationKey: 'settings.nav.payments' },
      // Was "Print Jobs". It is a queue you clear, and "queue" says so.
      {
        id: 'print-queue',
        path: '/settings/print-queue',
        translationKey: 'settings.nav.printQueue',
      },
    ],
  },
  {
    id: 'kiosk',
    translationKey: 'settings.nav.groups.kiosk',
    items: [
      { id: 'portal', path: '/settings/portal', translationKey: 'settings.nav.portal' },
      { id: 'games', path: '/settings/games', translationKey: 'settings.nav.games' },
      { id: 'apps', path: '/settings/apps', translationKey: 'settings.nav.apps' },
      {
        id: 'game-accounts',
        path: '/settings/game-accounts',
        translationKey: 'settings.nav.gameAccounts',
      },
    ],
  },
  {
    id: 'devices',
    translationKey: 'settings.nav.groups.devices',
    items: [
      // Device groups sat here too until the owner removed them (2026-09-24);
      // their old path redirects to this page.
      {
        id: 'device-categories',
        path: '/settings/device-categories',
        translationKey: 'settings.nav.deviceCategories',
      },
    ],
  },
  {
    id: 'people',
    translationKey: 'settings.nav.groups.people',
    items: [
      { id: 'employees', path: '/settings/employees', translationKey: 'settings.nav.employees' },
      { id: 'roles', path: '/settings/roles', translationKey: 'settings.nav.roles' },
    ],
  },
] as const;

/** Every settings entry, ungrouped — for contract tests and lookups. */
export const SETTINGS_NAV_ITEMS: readonly SettingsNavItem[] = SETTINGS_NAV_GROUPS.flatMap(
  (group) => [...group.items],
);

/**
 * The entry the settings area is showing — what the phone menu button names.
 * Exact path or a sub-path of it; null for anything the nav does not list
 * (a redirect mid-flight, a page reached only by URL).
 */
export function currentSettingsItem(pathname: string): SettingsNavItem | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return (
    SETTINGS_NAV_ITEMS.find((item) => path === item.path || path.startsWith(`${item.path}/`)) ??
    null
  );
}

/**
 * The one link out of the area, below the groups.
 *
 * Categories and Products used to sit in this list and redirect to /cafe.
 * Removing them without leaving a door would strand anyone whose habit is to
 * look for the menu under Settings; this reads as an exit, not as a setting.
 */
export const SETTINGS_EXIT_LINK: SettingsNavItem = {
  id: 'cafe-menu',
  path: '/cafe',
  translationKey: 'settings.nav.cafeMenu',
};
